import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { addError } from './Error';
import { getDetailsSearchResults, getSearchResults } from '../Api/Search/searchApi';

export enum EntityType {
  CUSTOMER = 'customer',
  ACCOUNT = 'rpm_account',
  ASSET = 'asset',
  TSD = 'tsd',
  AP = 'ap',
  SUPPLIER = 'supplier',
  RPM_SUPPLIER = 'rpm_supplier',
  TICKET = 'ticket',
  ADJUSTMENT = 'adjustment',
  PROVISIONAL_ASSET = 'provisional_asset',
  PRODUCT = 'product',
  RPM_PRODUCT = 'rpm_product',
  RPM_REP = 'rpm_rep',
  RPM_CUSTOMER_MAP = 'rpm_customer_map',
  PAYMENT_RECIPIENT = 'payment_recipient'
}

export type TypeAsset = EntityType.ASSET | EntityType.PROVISIONAL_ASSET;

export interface SearchResult {
  id: string;
  title: string;
  subTitle: string | null;
  searchBody: string | null;
  source: string;
  rank: number;
  matchedValue: string;
}

export interface FilterSearchDto {
  query: string;
  entityTypes?: string;
  limit?: number;
  filters?: Record<string, string | number | boolean>;
}

const searchResultSubjectMap: Record<string, { searchToken: number; search: BehaviorSubject<SearchResult[]> }> = {};
const searchResultsLoadingSubject = new BehaviorSubject<boolean>(false);
const searchDetailsResultsSubject = new BehaviorSubject<Record<string, object[]>>({});
const querySearchDetailsSubject = new BehaviorSubject<string>('');
const searchDetailsLoadingSubject = new BehaviorSubject<boolean>(false);

export const getSearchResultsSubject = (id: string): BehaviorSubject<SearchResult[]> | undefined => {
  return searchResultSubjectMap[id]?.search;
};

const getSearchResultsSubjectSearchToken = (id: string): number | undefined => {
  return searchResultSubjectMap[id]?.searchToken;
};

const updateSearchResultSubjectSearchToken = (id: string, tokenVal: number) => {
  searchResultSubjectMap[id].searchToken = tokenVal;
};

export const getSearchResultsLoadingSubject = (): BehaviorSubject<boolean> => searchResultsLoadingSubject;
export const getSearchDetailsResultsSubject = (): BehaviorSubject<Record<string, object[]>> => {
  return searchDetailsResultsSubject;
};
export const getSearchDetailsLoadingSubject = (): BehaviorSubject<boolean> => searchDetailsLoadingSubject;
export const addNewSearch = (id: string): BehaviorSubject<SearchResult[]> => {
  const searchResultsSubject = new BehaviorSubject<SearchResult[]>([]);
  searchResultSubjectMap[id] = { searchToken: 0, search: searchResultsSubject };
  return searchResultsSubject;
};

export const removeSearch = (id: string): void => {
  delete searchResultSubjectMap[id];
};

export const fetchSearchResults = async (id: string, searchDto: FilterSearchDto) => {
  const searchResultsSubject = getSearchResultsSubject(id);
  if (!searchResultsSubject) return;
  const requestSearchToken = (getSearchResultsSubjectSearchToken(id) as number) + 1;
  searchResultsLoadingSubject.next(true);

  const { data, error } = await getSearchResults(searchDto);
  const lastSearchToken = getSearchResultsSubjectSearchToken(id) as number;
  searchResultsLoadingSubject.next(false);

  if (data && requestSearchToken > lastSearchToken) {
    searchResultsSubject.next(data);
    updateSearchResultSubjectSearchToken(id, requestSearchToken);
    return data;
  }
  if (error) addError('search', error.message);
};

export const fetchSearchDetails = async (searchDto: FilterSearchDto) => {
  if (searchDetailsLoadingSubject.getValue()) return;
  searchDetailsLoadingSubject.next(true);
  if (searchDto.query === querySearchDetailsSubject.getValue()) {
    searchDetailsLoadingSubject.next(false);
    return;
  }
  querySearchDetailsSubject.next(searchDto.query || '');

  const { data, error } = await getDetailsSearchResults(searchDto);
  searchDetailsLoadingSubject.next(false);

  if (data) {
    searchDetailsResultsSubject.next(data);
  }
  if (error) addError('search', error.message);
};

export const clearSearchResultItems = (id: string) => {
  const searchResultsSubject = getSearchResultsSubject(id);
  if (!searchResultsSubject) return;
  searchResultsSubject.next([]);
};
